import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { TS_EditableItemComponent } from '@nu-art/thunderstorm-frontend/components/TS_EditableItemComponent/TS_EditableItemComponent';
import { Button, EditableDBItemV3, getElementCenterPos, LL_H_C, LL_V_L, ModuleFE_MouseInteractivity, mouseInteractivity_PopUp, openContent, TS_PropRenderer } from '@nu-art/thunderstorm-frontend';
import './Renderer_OrderRulesEditor.scss';
import { ICONS } from '@app/styles';
import { arrayIncludesAll, compare, dbObjectToId, deepClone, exists, filterDuplicates, sortArray } from '@nu-art/ts-common';
import { ModuleFE_ContextRule, ModuleFE_Expression, ModuleFE_Value, ModuleFE_Variable, Q_SortableList } from '@app/hcs-frontend';
import { Window_ExpressionEditor } from '@app/hcs-frontend/ui/_editors/expression/Window_ExpressionEditor/Window_ExpressionEditor';
import { Component_ExpressionPrettyPrinter } from '@app/hcs-frontend/ui/expressions/ui/expression-pretty-print/Component_ExpressionPrettyPrinter';
import { ModuleFE_AdvisorSnippet } from '@app/advisor-content-frontend/_entity/snippet/index';
import { DropDown_AdvisorSnippet } from '@app/advisor-content-frontend/_entity/snippet/ui-components';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { AccessLevel } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { ExpressionOutput_Boolean, ExpressionType_GeneralFunction } from '@app/hcs-shared';
import { Dialog_AssignOrderExpression } from './Dialog_AssignOrderExpression.js';
export class Renderer_OrderRulesEditor extends TS_EditableItemComponent {
    // ######################## Lifecycle ########################
    shouldComponentUpdate() {
        return true;
    }
    deriveStateFromProps(nextProps, state) {
        state = super.deriveStateFromProps(nextProps, state);
        state.editMode = nextProps.editMode;
        return state;
    }
    // ######################## Logic ########################
    getVariablesForRule = (rule, excludedVariables) => {
        return ModuleFE_Variable.cache.filter(variable => {
            if (excludedVariables.includes(variable._id))
                return false;
            return arrayIncludesAll(variable._tagIds, rule.tagIds);
        });
    };
    isAlphabeticallySorted = (values) => {
        const namesArray = values.map(id => ModuleFE_Variable.cache.unique(id)._nameInLowercase);
        const sortedNamesArray = [...namesArray].sort();
        return sortedNamesArray.every((name, index) => name === namesArray[index]);
    };
    handleShorterList = (prevArray, newArray) => {
        // if subset of the prev array and the array if custom sorted just filter out irrelevant variables
        if (arrayIncludesAll(prevArray, newArray) && !this.isAlphabeticallySorted(prevArray)) {
            return this.state.editable.update('relatedVariables', prevArray.filter(id => newArray.includes(id)));
        }
        // if not fitting the prev array revert all to a-z sort over new variables
        return this.state.editable.update('relatedVariables', newArray);
    };
    handleLongerList = (prevArray, newArray) => {
        // handle a case that the new array is a longer list including all prev variables
        if (arrayIncludesAll(newArray, prevArray)) {
            const newValues = filterDuplicates([...prevArray, ...newArray]);
            // if sorted a-z keep it sorted and merge the arrays
            if (this.isAlphabeticallySorted(prevArray)) {
                return this.state.editable.update('relatedVariables', sortArray(newValues, item => ModuleFE_Variable.cache.unique(item)?._nameInLowercase));
            }
            // otherwise find the diff and push it to the end of the list
            const arrayDiff = newArray.filter(id => !prevArray.includes(id));
            return this.state.editable.update('relatedVariables', [...prevArray, ...arrayDiff]);
        }
        // if it's a new array set it like initial mode!
        return this.state.editable.update('relatedVariables', newArray);
    };
    updateRelatedVariables = async () => {
        const snippet = ModuleFE_AdvisorSnippet.cache.unique(this.state.editable.item.snippetId);
        const contextRules = ModuleFE_ContextRule.cache.unique(snippet?.contextRuleId);
        const rules = contextRules?.rules ?? [];
        const prevVariables = this.state.editable.item.relatedVariables;
        const collected = rules.map(rule => this.getVariablesForRule(rule, contextRules?.excludedVariableIds ?? [])).flat();
        const filtered = filterDuplicates(collected, dbObjectToId).filter(dbVar => dbVar._id !== this.state.editable.item.variableId);
        // if removing all rules clear variables
        if (!rules || !rules.length || !filtered.length)
            return this.state.editable.update('relatedVariables', undefined);
        // initially set variables sorted a-z
        if (!prevVariables || !prevVariables.length) {
            return this.state.editable.update('relatedVariables', sortArray(filtered, filtered => filtered._nameInLowercase).map(dbObjectToId));
        }
        // add more variables conditionally
        const newVariableIds = sortArray(filtered, item => item._nameInLowercase).map(dbObjectToId);
        if (newVariableIds.length < prevVariables.length) {
            return this.handleShorterList(prevVariables, newVariableIds);
        }
        if (newVariableIds.length > prevVariables.length) {
            return this.handleLongerList(prevVariables, newVariableIds);
        }
    };
    getToolTip = (text) => {
        return openContent.tooltip.top('order-rules-editor-help-tooltip', text, { offset: 10 });
    };
    getMode = () => {
        return this.state.editMode ? 'edit' : 'view';
    };
    openExpressionEditor = () => {
        const expressionId = this.state.editable.item.expressionId;
        if (!expressionId)
            return;
        const canEdit = ModuleFE_Expression.permissions.collection.edit.getAccessLevel() === AccessLevel.HasAccess;
        Window_ExpressionEditor.show({
            expressionId,
            editMode: canEdit,
            editorConfig: {
                label: true,
                terms: true,
                outputType: false,
                output: true,
            },
        });
    };
    openCreateExpression = () => {
        const canEdit = ModuleFE_Expression.permissions.collection.edit.getAccessLevel() === AccessLevel.HasAccess;
        Window_ExpressionEditor.show({
            editMode: canEdit,
            editorConfig: {
                label: true,
                terms: true,
                outputType: false,
                output: true,
            },
            initialData: {
                expressionType: ExpressionType_GeneralFunction.dbKey,
                outputType: ExpressionOutput_Boolean,
                tagGroups: [],
            },
            onSaved: async (saved) => {
                await this.state.editable.update('expressionId', saved._id);
            },
        });
    };
    openAssignExpressionDialog = () => {
        Dialog_AssignOrderExpression.show({
            expressionId: this.state.editable.item.expressionId,
            onSave: async (expressionId) => this.state.editable.update('expressionId', expressionId),
        });
    };
    openExpressionMenu = (e) => {
        const model = {
            id: 'pop-up__expression-menu',
            originPos: getElementCenterPos(e.currentTarget),
            modalPos: { x: -1, y: 1 },
            xAxisAnchor: 'right',
            offset: { x: 8 },
            content: () => _jsxs(_Fragment, { children: [_jsx(Button, { variant: 'text', onClick: () => {
                            this.openAssignExpressionDialog();
                            ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                        }, children: "Assign existing expression" }), _jsx(Button, { variant: 'text', onClick: () => {
                            this.openCreateExpression();
                            ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                        }, children: "Create new expression" }), exists(this.state.editable.item.expressionId) &&
                        _jsx(Button, { variant: 'text', onClick: async () => {
                                await this.state.editable.update('expressionId', undefined);
                                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                            }, children: "Clear" })] }),
        };
        ModuleFE_MouseInteractivity.showContent(model);
    };
    // ######################## Render ########################
    renderSortableList = () => {
        const relatedVariables = (deepClone(this.state.editable.get('relatedVariables')) ?? []);
        const label = () => {
            return _jsxs(_Fragment, { children: ["Related Variables (", relatedVariables.length, ")", _jsx(ICONS.information.component, { className: 'info', ...this.getToolTip('All variables resolved from rules, drag each item to re-organize the list. (default sort is a-z)') }), this.state.editMode &&
                        _jsx(ICONS.sync.component, { onClick: this.updateRelatedVariables, className: 'sync-vars', ...this.getToolTip('Update the variable set from the variables that pass the rules') })] });
        };
        return _jsx(TS_PropRenderer.Vertical, { className: 'draggable-list', label: label, children: _jsx(Q_SortableList, { items: relatedVariables, disabled: !this.state.editMode, itemRenderer: (item) => _jsx(LL_H_C, { className: 'related-variable-item', children: ModuleFE_Variable.cache.unique(item)?.name ?? 'N/A' }), onChange: async (order) => {
                    if (!compare(order, relatedVariables)) // only update if order changed
                        await this.state.editable.update('relatedVariables', order);
                } }) });
    };
    renderSnippetSelector = () => {
        const dbSnippet = ModuleFE_AdvisorSnippet.cache.unique(this.state.editable.item.snippetId);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Select Related Snippet', children: _jsx(Component_SwitchView, { mode: this.getMode(), modes: [
                    {
                        key: 'view',
                        renderer: () => _jsx(_Fragment, { children: dbSnippet?.label ?? '-' })
                    },
                    {
                        key: 'edit',
                        renderer: () => _jsx(DropDown_AdvisorSnippet.editable, { disabled: !this.state.editMode, editable: this.state.editable, prop: 'snippetId' })
                    }
                ] }) });
    };
    renderExpressionSelector = () => {
        const expressionId = this.state.editable.item.expressionId;
        const expression = ModuleFE_Expression.cache.unique(expressionId);
        const editableExpression = new EditableDBItemV3(expression ?? {}, ModuleFE_Expression);
        const expressionLabel = expression?.label ? `Expression ID - ${expression.label}` : 'Expression ID';
        return _jsx(TS_PropRenderer.Vertical, { label: expressionLabel, children: _jsx(Component_SwitchView, { mode: this.getMode(), modes: [
                    {
                        key: 'view',
                        renderer: () => !exists(expressionId) || !expression
                            ? _jsx(_Fragment, { children: '-' })
                            : _jsxs(LL_H_C, { className: 'order-editor__expression-row', children: [_jsx(ICONS.expressions.component, { className: 'order-editor__expression-open', onClick: this.openExpressionEditor }), _jsx(Component_ExpressionPrettyPrinter, { editable: editableExpression, showHeader: false, variablesModule: ModuleFE_Variable, valueModule: ModuleFE_Value, expressionsModule: ModuleFE_Expression })] })
                    },
                    {
                        key: 'edit',
                        renderer: () => !exists(expressionId) || !expression
                            ? _jsxs(LL_H_C, { className: 'order-editor__expression-selector', children: [_jsx("span", { className: 'order-editor__expression-placeholder', children: "No expression assigned" }), _jsx(ICONS.more.component, { className: 'order-editor__expression-menu', onClick: this.openExpressionMenu })] })
                            : _jsxs(LL_H_C, { className: 'order-editor__expression-row', children: [_jsx(ICONS.expressions.component, { className: 'order-editor__expression-open', onClick: this.openExpressionEditor }), _jsx(Component_ExpressionPrettyPrinter, { editable: editableExpression, showHeader: false, variablesModule: ModuleFE_Variable, valueModule: ModuleFE_Value, expressionsModule: ModuleFE_Expression }), _jsx(ICONS.more.component, { className: 'order-editor__expression-menu', onClick: this.openExpressionMenu })] })
                    }
                ] }) });
    };
    render() {
        return _jsx(LL_V_L, { id: 'order-editor__tag-rules-and-variables', children: _jsxs(LL_H_C, { className: 'order-editor__base-content', children: [this.renderSnippetSelector(), this.renderExpressionSelector(), this.renderSortableList()] }) });
    }
}
//# sourceMappingURL=Renderer_OrderRulesEditor.js.map